import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { Settings } from '../../../interfaces/Settings/Settings';
import { getSettingsByType, upsertSettings } from '../../../api/Settings';
import { SettingsType } from '../../../interfaces/Settings/SettingsType';
import { GenericSettingsValue } from '../../../interfaces/Settings/GenericSettingsValue';
import { getSettingsValue, getUpdatedSettings } from '../../../utils/Settings';
import { Waiting } from '../../../components/Waiting/Waiting';
import { sharedStyles } from '../../../utils/Style';
import { SettingsRow } from '../../../components/Settings/SettingsRow';
import { SettingsValueType } from '../../../interfaces/Settings/SettingsValueType';
import { checkHepsiburadaStatus } from '../../../api/Hepsiburada';
import { IntegrationStatusBadge } from '../../../components/IntegrationStatus/IntegrationStatusBadge';
import { PartnerPlatformSettingsProps } from '../../../interfaces/Settings/PartnerPlatformSettingsProps';

export const HepsiburadaSettings = ({
  onSave,
}: PartnerPlatformSettingsProps) => {
  const [settings, setSettings] = useState<Settings[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );
  const [loadingStatus, setLoadingStatus] = useState(true);

  const refreshSettings = () => {
    setLoading(true);
    getSettingsByType(SettingsType.HEPSIBURADA)
      .then((s) => setSettings(s))
      .catch((err) => toast.error(err.message))
      .finally(() => {
        refreshStatus();
        setLoading(false);
      });
  };

  const refreshStatus = () => {
    setLoadingStatus(true);
    checkHepsiburadaStatus()
      .then(() => setErrorMessage(undefined))
      .catch((err) => {
        setErrorMessage(err.message ?? 'Bilinmeyen hata');
      })
      .finally(() => setLoadingStatus(false));
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  const handleChange = useCallback(
    (key: string, newValue: GenericSettingsValue) =>
      setSettings(
        getUpdatedSettings(SettingsType.HEPSIBURADA, key, settings, newValue),
      ),
    [settings],
  );

  const handleSave = () => {
    setLoading(true);
    upsertSettings(settings)
      .then(() => {
        toast.success('Ayarlar başarıyla kaydedildi');
        refreshSettings();
        onSave?.();
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '95%',
      }}
    >
      <Waiting open={loading} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            ...sharedStyles.h5,
            fontWeight: 'bold',
          }}
        >
          Hepsiburada
        </Typography>

        <IntegrationStatusBadge
          status={
            loadingStatus ? 'loading' : errorMessage ? 'error' : 'success'
          }
          errorMessage={errorMessage}
        />
      </Box>
      <Divider />

      <SettingsRow
        name='Stok ve Fiyat Eşitleme'
        valueType={SettingsValueType.BOOLEAN}
        value={getSettingsValue(settings, 'hepsiburada_status')}
        onChange={(newValue) => handleChange('hepsiburada_status', newValue)}
      />
      <SettingsRow
        name='Komisyon Oranı (%)'
        valueType={SettingsValueType.NUMBER}
        value={getSettingsValue(settings, 'hepsiburada_commision')}
        onChange={(newValue) => handleChange('hepsiburada_commision', newValue)}
        minValue={0}
        maxValue={100}
      />
      <SettingsRow
        name='Listeleme URL'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'hepsiburada_url')}
        onChange={(newValue) => handleChange('hepsiburada_url', newValue)}
      />
      <SettingsRow
        name='Sipariş URL'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'hepsiburada_order_url')}
        onChange={(newValue) => handleChange('hepsiburada_order_url', newValue)}
      />
      <SettingsRow
        name='Kullanıcı Adı'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'hepsiburada_username')}
        onChange={(newValue) => handleChange('hepsiburada_username', newValue)}
      />
      <SettingsRow
        name='Şifre'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'hepsiburada_password')}
        onChange={(newValue) => handleChange('hepsiburada_password', newValue)}
        masked
      />
      <SettingsRow
        name='Satıcı ID'
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(settings, 'hepsiburada_merchant_id')}
        onChange={(newValue) =>
          handleChange('hepsiburada_merchant_id', newValue)
        }
      />

      <Box sx={{ display: 'flex', flexGrow: 1 }} />
      <Box display='flex' justifyContent='flex-end'>
        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText, mt: 1.5 }}
        >
          Kaydet
        </Button>
      </Box>
    </Paper>
  );
};
